import type { KeptScan, ScanAgentApi } from './scan-agent';
import { holdPage, releasePages, type HeldPage } from './scan-pages';

/**
 * Pages Paige Scan kept on disk for a scan whose tab closed, reloaded or crashed before they were added to the job
 * (cpv6-parity I-12; `apps/scan/Paige.Scan/Agent/KeptScans.cs`). The sheet offers them when it opens; an older Paige
 * Scan has none to offer, and the sheet is then exactly as it was.
 */

/**
 * The kept scans this sheet offers: only those scanned for this very job, never one this sheet already holds. A
 * scan with no job, or a sheet with none, offers nothing: one computer's browser may be signed in to more than one
 * company, and a kept page must never be offered to a company it was not scanned for. Nothing from a Paige Scan before 1.5.22 (it answers 404) or on any failure: no error is shown.
 */
export async function keptScansFor(
  api: Pick<ScanAgentApi, 'keptScans'>,
  jobId: string | null,
  own: ReadonlySet<string> = new Set(),
): Promise<KeptScan[]> {
  try {
    const kept = await api.keptScans();
    return kept.filter(
      (k) => k.pages > 0 && !own.has(k.scan) && jobId !== null && k.jobId === jobId,
    );
  } catch {
    return [];
  }
}

/**
 * A kept scan's pages, fetched one at a time and held as a scan's are (Blobs, object-URL previews). All or nothing:
 * a page that cannot be fetched gives back the previews already made and rejects, so trying again never doubles a page.
 */
export async function fetchKeptPages(
  api: Pick<ScanAgentApi, 'keptPage'>,
  kept: KeptScan,
): Promise<HeldPage[]> {
  const pages: HeldPage[] = [];
  try {
    for (let index = 1; index <= kept.pages; index++) {
      pages.push(holdPage(await api.keptPage(kept.scan, index)));
    }
    return pages;
  } catch (error) {
    releasePages(pages);
    throw error;
  }
}

/** The next index for a page the sheet holds: one past the highest, so every scan's pages stay distinct. */
export function nextHeldIndex(pages: readonly HeldPage[]): number {
  return pages.reduce((most, page) => Math.max(most, page.index), 0) + 1;
}

/** "84 pages from a scan at 2:14 PM were not added." (with the day when it was not today). */
export function keptLine(kept: KeptScan, now: Date = new Date(), locale?: string): string {
  const at = new Date(kept.scannedAt);
  const time = at.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  const today = at.toDateString() === now.toDateString();
  const when = today
    ? `at ${time}`
    : `on ${at.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} at ${time}`;
  const count = `${kept.pages.toLocaleString(locale)} page${kept.pages === 1 ? '' : 's'}`;
  return `${count} from a scan ${when} ${kept.pages === 1 ? 'was' : 'were'} not added.`;
}
